import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Enhanced Product Selector Component
 *
 * Multi-select product component with category/tag filters,
 * pagination, and checkbox bulk selection.
 *
 * @package WooAIChatbot
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Package, ChevronLeft, ChevronRight, Check, Plus } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation';
import { productsApi, categoriesApi, tagsApi, } from '../../../utils/api';
// Inline styles matching the PromotionEditor design
const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    filtersRow: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
    },
    searchContainer: {
        position: 'relative',
        flex: '1',
        minWidth: '200px',
    },
    searchIcon: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#9ca3af',
        pointerEvents: 'none',
    },
    searchInput: {
        width: '100%',
        height: '40px',
        paddingLeft: '40px',
        paddingRight: '12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        color: '#111827',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        outline: 'none',
    },
    searchInputFocused: {
        borderColor: '#6366f1',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
    },
    select: {
        height: '40px',
        minWidth: '160px',
        paddingLeft: '12px',
        paddingRight: '32px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        color: '#111827',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M2.5 4.5l3.5 3.5 3.5-3.5'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
    },
    productList: {
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
    },
    selectAllRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
    },
    checkbox: {
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        border: '2px solid #d1d5db',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'all 0.15s',
    },
    checkboxChecked: {
        backgroundColor: '#6366f1',
        borderColor: '#6366f1',
    },
    productRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderBottom: '1px solid #f3f4f6',
        transition: 'background-color 0.15s',
        cursor: 'pointer',
    },
    productRowHover: {
        backgroundColor: '#f9fafb',
    },
    productRowChecked: {
        backgroundColor: '#eef2ff',
    },
    productImage: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        objectFit: 'cover',
        backgroundColor: '#f3f4f6',
        flexShrink: 0,
    },
    productImagePlaceholder: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        backgroundColor: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    productInfo: {
        flex: 1,
        minWidth: 0,
    },
    productName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827',
        marginBottom: '4px',
    },
    productMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    productPrice: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#6366f1',
    },
    productTags: {
        display: 'flex',
        gap: '4px',
        flexWrap: 'wrap',
    },
    tag: {
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 500,
        backgroundColor: '#e5e7eb',
        color: '#374151',
    },
    tagCategory: {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
    },
    tagProductTag: {
        backgroundColor: '#fef3c7',
        color: '#b45309',
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
    },
    paginationButton: {
        width: '32px',
        height: '32px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#374151',
    },
    paginationButtonDisabled: {
        opacity: 0.5,
        cursor: 'default',
    },
    paginationText: {
        fontSize: '14px',
        color: '#6b7280',
        padding: '0 8px',
    },
    selectedFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: '#f0fdf4',
        borderRadius: '8px',
        border: '1px solid #bbf7d0',
    },
    selectedCount: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#15803d',
    },
    addSelectedButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background-color 0.15s',
    },
    addSelectedButtonDisabled: {
        backgroundColor: '#d1d5db',
        cursor: 'default',
    },
    selectedProducts: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '8px',
    },
    selectedProduct: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 12px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
    },
    removeButton: {
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#dc2626',
        flexShrink: 0,
    },
    emptyState: {
        padding: '48px 16px',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '14px',
    },
    loadingState: {
        padding: '48px 16px',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '14px',
    },
    sectionLabel: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#374151',
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
};
export function ProductSelector({ selectedIds, onChange, placeholder = 'Search products...', }) {
    const { t } = useTranslation();
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [tagFilter, setTagFilter] = useState('');
    const [page, setPage] = useState(1);
    const perPage = 20;
    // Data states
    const [searchResult, setSearchResult] = useState({
        products: [],
        total: 0,
        pages: 0,
        page: 1,
        perPage: 20,
    });
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    // UI states
    const [checkedIds, setCheckedIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFocused, setIsFocused] = useState(false);
    const [hoveredId, setHoveredId] = useState(null);
    // Ref for debounce
    const searchTimeoutRef = useRef(null);
    // Load categories and tags on mount
    useEffect(() => {
        const loadFilters = async () => {
            try {
                const [cats, tgs] = await Promise.all([
                    categoriesApi.getAll(),
                    tagsApi.getAll(),
                ]);
                setCategories(cats);
                setTags(tgs);
            }
            catch (error) {
                console.error('Failed to load filters:', error);
            }
        };
        loadFilters();
    }, []);
    // Load products with filters
    const loadProducts = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await productsApi.search({
                search: searchTerm || undefined,
                category: categoryFilter || undefined,
                tag: tagFilter || undefined,
                page,
                perPage,
            });
            setSearchResult(result);
        }
        catch (error) {
            console.error('Failed to load products:', error);
            setSearchResult({ products: [], total: 0, pages: 0, page: 1, perPage: 20 });
        }
        finally {
            setIsLoading(false);
        }
    }, [searchTerm, categoryFilter, tagFilter, page, perPage]);
    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            loadProducts();
        }, 300);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [loadProducts]);
    // Reset page when filters change
    useEffect(() => {
        setPage(1);
        setCheckedIds([]);
    }, [searchTerm, categoryFilter, tagFilter]);
    // Load selected products
    useEffect(() => {
        const loadSelectedProducts = async () => {
            if (selectedIds.length === 0) {
                setSelectedProducts([]);
                return;
            }
            try {
                const products = await productsApi.getByIds(selectedIds);
                setSelectedProducts(products);
            }
            catch (error) {
                console.error('Failed to load selected products:', error);
            }
        };
        loadSelectedProducts();
    }, [selectedIds]);
    // Handlers
    const handleCheckProduct = (productId) => {
        if (checkedIds.includes(productId)) {
            setCheckedIds(checkedIds.filter((id) => id !== productId));
        }
        else {
            setCheckedIds([...checkedIds, productId]);
        }
    };
    const handleSelectAll = () => {
        const pageProductIds = searchResult.products.map((p) => p.id);
        const allChecked = pageProductIds.every((id) => checkedIds.includes(id));
        if (allChecked) {
            // Uncheck all on page
            setCheckedIds(checkedIds.filter((id) => !pageProductIds.includes(id)));
        }
        else {
            // Check all on page
            const newChecked = new Set([...checkedIds, ...pageProductIds]);
            setCheckedIds(Array.from(newChecked));
        }
    };
    const handleAddSelected = () => {
        // Add checked products to selected (exclude already selected)
        const newIds = checkedIds.filter((id) => !selectedIds.includes(id));
        if (newIds.length > 0) {
            onChange([...selectedIds, ...newIds]);
        }
        setCheckedIds([]);
    };
    const handleRemoveProduct = (productId) => {
        onChange(selectedIds.filter((id) => id !== productId));
    };
    const isProductSelected = (productId) => selectedIds.includes(productId);
    const pageProductIds = searchResult.products.map((p) => p.id);
    const allPageChecked = pageProductIds.length > 0 && pageProductIds.every((id) => checkedIds.includes(id));
    const somePageChecked = pageProductIds.some((id) => checkedIds.includes(id)) && !allPageChecked;
    const checkedNotSelected = checkedIds.filter((id) => !selectedIds.includes(id));
    return (_jsxs("div", { style: styles.container, children: [_jsxs("div", { style: styles.filtersRow, children: [_jsxs("div", { style: styles.searchContainer, children: [_jsx(Search, { style: { ...styles.searchIcon, width: '16px', height: '16px' } }), _jsx("input", { type: "text", style: {
                                    ...styles.searchInput,
                                    ...(isFocused ? styles.searchInputFocused : {}),
                                }, placeholder: placeholder, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false) })] }), _jsxs("select", { style: styles.select, value: categoryFilter, onChange: (e) => setCategoryFilter(e.target.value), children: [_jsx("option", { value: "", children: t('allCategories') }), categories.map((cat) => (_jsxs("option", { value: cat.slug, children: [cat.name, " (", cat.count, ")"] }, cat.id)))] }), _jsxs("select", { style: styles.select, value: tagFilter, onChange: (e) => setTagFilter(e.target.value), children: [_jsx("option", { value: "", children: t('allTags') }), tags.map((tag) => (_jsxs("option", { value: tag.slug, children: [tag.name, " (", tag.count, ")"] }, tag.id)))] })] }), _jsxs("div", { style: styles.productList, children: [_jsxs("div", { style: styles.selectAllRow, children: [_jsx("div", { style: {
                                    ...styles.checkbox,
                                    ...(allPageChecked || somePageChecked ? styles.checkboxChecked : {}),
                                }, onClick: handleSelectAll, children: (allPageChecked || somePageChecked) && (_jsx(Check, { style: { width: '12px', height: '12px', color: '#ffffff' } })) }), _jsx("span", { style: { fontSize: '13px', color: '#6b7280' }, children: allPageChecked ? t('deselectAllOnPage') : t('selectAllOnPage') })] }), isLoading ? (_jsx("div", { style: styles.loadingState, children: t('loadingProducts') })) : searchResult.products.length === 0 ? (_jsx("div", { style: styles.emptyState, children: t('noProductsFound') })) : (searchResult.products.map((product) => {
                        const isChecked = checkedIds.includes(product.id);
                        const isSelected = isProductSelected(product.id);
                        const isHovered = hoveredId === product.id;
                        return (_jsxs("div", { style: {
                                ...styles.productRow,
                                ...(isHovered ? styles.productRowHover : {}),
                                ...(isChecked ? styles.productRowChecked : {}),
                            }, onMouseEnter: () => setHoveredId(product.id), onMouseLeave: () => setHoveredId(null), onClick: () => !isSelected && handleCheckProduct(product.id), children: [_jsx("div", { style: {
                                        ...styles.checkbox,
                                        ...(isChecked || isSelected ? styles.checkboxChecked : {}),
                                        ...(isSelected ? { opacity: 0.5, cursor: 'default' } : {}),
                                    }, onClick: (e) => {
                                        e.stopPropagation();
                                        if (!isSelected)
                                            handleCheckProduct(product.id);
                                    }, children: (isChecked || isSelected) && (_jsx(Check, { style: { width: '12px', height: '12px', color: '#ffffff' } })) }), product.image ? (_jsx("img", { src: product.image, alt: "", style: styles.productImage })) : (_jsx("div", { style: styles.productImagePlaceholder, children: _jsx(Package, { style: { width: '24px', height: '24px', color: '#9ca3af' } }) })), _jsxs("div", { style: styles.productInfo, children: [_jsxs("div", { style: styles.productName, children: [product.name, isSelected && (_jsxs("span", { style: { marginLeft: '8px', fontSize: '11px', color: '#15803d' }, children: ["(", t('alreadyAdded'), ")"] }))] }), _jsxs("div", { style: styles.productMeta, children: [_jsx("span", { style: styles.productPrice, children: product.price }), _jsxs("div", { style: styles.productTags, children: [product.categories?.slice(0, 2).map((cat, i) => (_jsx("span", { style: { ...styles.tag, ...styles.tagCategory }, children: cat }, `cat-${i}`))), product.tags?.slice(0, 2).map((tag, i) => (_jsx("span", { style: { ...styles.tag, ...styles.tagProductTag }, children: tag }, `tag-${i}`)))] })] })] })] }, product.id));
                    })), searchResult.pages > 1 && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { style: {
                                    ...styles.paginationButton,
                                    ...(page <= 1 ? styles.paginationButtonDisabled : {}),
                                }, disabled: page <= 1, onClick: () => setPage((p) => Math.max(1, p - 1)), children: _jsx(ChevronLeft, { style: { width: '16px', height: '16px' } }) }), _jsxs("span", { style: styles.paginationText, children: [t('page'), " ", page, " ", t('of'), " ", searchResult.pages] }), _jsx("button", { style: {
                                    ...styles.paginationButton,
                                    ...(page >= searchResult.pages ? styles.paginationButtonDisabled : {}),
                                }, disabled: page >= searchResult.pages, onClick: () => setPage((p) => Math.min(searchResult.pages, p + 1)), children: _jsx(ChevronRight, { style: { width: '16px', height: '16px' } }) })] }))] }), checkedIds.length > 0 && (_jsxs("div", { style: styles.selectedFooter, children: [_jsxs("div", { style: styles.selectedCount, children: [_jsx(Check, { style: { width: '16px', height: '16px' } }), t('selected'), ": ", checkedIds.length, " ", t('products'), checkedNotSelected.length < checkedIds.length && (_jsxs("span", { style: { color: '#6b7280', fontWeight: 400 }, children: ["(", checkedIds.length - checkedNotSelected.length, " ", t('alreadyAdded'), ")"] }))] }), _jsxs("button", { style: {
                            ...styles.addSelectedButton,
                            ...(checkedNotSelected.length === 0 ? styles.addSelectedButtonDisabled : {}),
                        }, disabled: checkedNotSelected.length === 0, onClick: handleAddSelected, children: [_jsx(Plus, { style: { width: '16px', height: '16px' } }), t('addSelected'), " (", checkedNotSelected.length, ")"] })] })), selectedProducts.length > 0 && (_jsxs("div", { children: [_jsxs("div", { style: styles.sectionLabel, children: [t('addedProducts'), " (", selectedProducts.length, ")"] }), _jsx("div", { style: styles.selectedProducts, children: selectedProducts.map((product) => (_jsxs("div", { style: styles.selectedProduct, children: [product.image ? (_jsx("img", { src: product.image, alt: "", style: { ...styles.productImage, width: '40px', height: '40px' } })) : (_jsx("div", { style: { ...styles.productImagePlaceholder, width: '40px', height: '40px' }, children: _jsx(Package, { style: { width: '20px', height: '20px', color: '#9ca3af' } }) })), _jsxs("div", { style: styles.productInfo, children: [_jsx("div", { style: { ...styles.productName, fontSize: '13px' }, children: product.name }), _jsx("div", { style: { fontSize: '12px', color: '#6b7280' }, children: product.price })] }), _jsx("button", { style: styles.removeButton, onClick: () => handleRemoveProduct(product.id), title: t('removeProduct'), children: _jsx(X, { style: { width: '16px', height: '16px' } }) })] }, product.id))) })] }))] }));
}
export default ProductSelector;
